import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { SegmentClosed } from "../api";
import { SEGMENT_LABEL, type Segment } from "../constants";
import { fmtCentsAccounting } from "../format";
import { DeltaCell } from "@/components/cinch/DeltaCell";

interface SegmentRow {
  closed_cents: number;
  forecast_cents: number;
  upside_cents: number;
}

function indexRows(rows: SegmentClosed[]): Record<string, SegmentRow> {
  const out: Record<string, SegmentRow> = {};
  for (const r of rows) {
    const ex = out[r.segment] ?? { closed_cents: 0, forecast_cents: 0, upside_cents: 0 };
    out[r.segment] = {
      closed_cents: (ex.closed_cents || 0) + (r.closed_cents || 0),
      forecast_cents: (ex.forecast_cents || 0) + (r.forecast_cents || 0),
      upside_cents: (ex.upside_cents || 0) + (r.upside_cents || 0),
    };
  }
  return out;
}

/**
 * New Revenue table — 4 segments × Closed / Forecast / Upside, each with a
 * prior-week column + delta. Matches the layout in the Week-11 HTML
 * (Direct NL / Direct Reseller / Upsell Reseller / Upsell & Replacement).
 *
 * Prior-week values are pulled from a snapshot if available; if not, the
 * columns render em-dashes and the FE quietly skips deltas.
 */
export function NewRevenueTable({
  title,
  priorLabel,
  closed,
  forecast,
  upside,
  priorClosed,
  priorForecast,
  priorUpside,
}: {
  title: string;
  /** Label for the prior-period column (e.g. "Wk 10"). */
  priorLabel: string;
  closed: SegmentClosed[];
  forecast: SegmentClosed[];
  upside: SegmentClosed[];
  priorClosed?: SegmentClosed[];
  priorForecast?: SegmentClosed[];
  priorUpside?: SegmentClosed[];
}) {
  const closedIdx = indexRows(closed);
  const forecastIdx = indexRows(forecast);
  const upsideIdx = indexRows(upside);
  const priorClosedIdx = priorClosed ? indexRows(priorClosed) : null;
  const priorForecastIdx = priorForecast ? indexRows(priorForecast) : null;
  const priorUpsideIdx = priorUpside ? indexRows(priorUpside) : null;

  const segments: Segment[] = [
    "direct_nl",
    "direct_reseller",
    "upsell_reseller",
    "upsell_replacement",
  ];

  function rowFor(seg: Segment) {
    return {
      closed: closedIdx[seg]?.closed_cents ?? 0,
      forecast: forecastIdx[seg]?.forecast_cents ?? 0,
      upside: upsideIdx[seg]?.upside_cents ?? 0,
      priorClosed: priorClosedIdx?.[seg]?.closed_cents ?? null,
      priorForecast: priorForecastIdx?.[seg]?.forecast_cents ?? null,
      priorUpside: priorUpsideIdx?.[seg]?.upside_cents ?? null,
    };
  }

  const totals = segments.reduce(
    (acc, s) => {
      const r = rowFor(s);
      return {
        closed: acc.closed + r.closed,
        forecast: acc.forecast + r.forecast,
        upside: acc.upside + r.upside,
        priorClosed: acc.priorClosed + (r.priorClosed ?? 0),
        priorForecast: acc.priorForecast + (r.priorForecast ?? 0),
        priorUpside: acc.priorUpside + (r.priorUpside ?? 0),
        anyPrior:
          acc.anyPrior ||
          r.priorClosed != null ||
          r.priorForecast != null ||
          r.priorUpside != null,
      };
    },
    {
      closed: 0,
      forecast: 0,
      upside: 0,
      priorClosed: 0,
      priorForecast: 0,
      priorUpside: 0,
      anyPrior: false,
    },
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Segment</TableHead>
              <TableHead className="text-right">Closed</TableHead>
              <TableHead className="text-right text-power/70">{priorLabel}</TableHead>
              <TableHead className="text-right text-power/70">Δ</TableHead>
              <TableHead className="text-right">Forecast</TableHead>
              <TableHead className="text-right text-power/70">{priorLabel}</TableHead>
              <TableHead className="text-right text-power/70">Δ</TableHead>
              <TableHead className="text-right">Upside</TableHead>
              <TableHead className="text-right text-power/70">{priorLabel}</TableHead>
              <TableHead className="text-right text-power/70">Δ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {segments.map((s) => {
              const r = rowFor(s);
              return (
                <TableRow key={s}>
                  <TableCell className="font-medium">{SEGMENT_LABEL[s]}</TableCell>
                  <TableCell className="text-right font-mono">
                    {fmtCentsAccounting(r.closed)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">
                    {fmtCentsAccounting(r.priorClosed)}
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    <DeltaCell value={r.priorClosed != null ? r.closed - r.priorClosed : null} />
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {fmtCentsAccounting(r.forecast)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">
                    {fmtCentsAccounting(r.priorForecast)}
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    <DeltaCell value={r.priorForecast != null ? r.forecast - r.priorForecast : null} />
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {fmtCentsAccounting(r.upside)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">
                    {fmtCentsAccounting(r.priorUpside)}
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    <DeltaCell value={r.priorUpside != null ? r.upside - r.priorUpside : null} />
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow className="bg-muted/40 font-medium">
              <TableCell>Subtotal</TableCell>
              <TableCell className="text-right font-mono">
                {fmtCentsAccounting(totals.closed)}
              </TableCell>
              <TableCell className="text-right font-mono text-muted-foreground">
                {totals.anyPrior ? fmtCentsAccounting(totals.priorClosed) : "—"}
              </TableCell>
              <TableCell className="text-right text-xs">
                <DeltaCell value={totals.anyPrior ? totals.closed - totals.priorClosed : null} />
              </TableCell>
              <TableCell className="text-right font-mono">
                {fmtCentsAccounting(totals.forecast)}
              </TableCell>
              <TableCell className="text-right font-mono text-muted-foreground">
                {totals.anyPrior ? fmtCentsAccounting(totals.priorForecast) : "—"}
              </TableCell>
              <TableCell className="text-right text-xs">
                <DeltaCell value={totals.anyPrior ? totals.forecast - totals.priorForecast : null} />
              </TableCell>
              <TableCell className="text-right font-mono">
                {fmtCentsAccounting(totals.upside)}
              </TableCell>
              <TableCell className="text-right font-mono text-muted-foreground">
                {totals.anyPrior ? fmtCentsAccounting(totals.priorUpside) : "—"}
              </TableCell>
              <TableCell className="text-right text-xs">
                <DeltaCell value={totals.anyPrior ? totals.upside - totals.priorUpside : null} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
